import { defaultsToCss, defaultsToVars } from '@emico-utils/css-var-utils'

export const libraryThemeDefaults = {
  /**
   * Accent color, to be used for controls and to accent certain parts of a component. For example
   * a checkbox active state could use this color. Or indicators in the component slider.
   */
  accentColor: '#ff1053',
  /**
   * Color that should be used when text or shapes are rendered on an accent color.
   */
  onAccentColor: '#fff',
}

const prefix = 'emico'

export const libraryThemeCss = defaultsToCss(libraryThemeDefaults, prefix)
const libraryThemeVars = defaultsToVars(libraryThemeDefaults, prefix)

function withKey(overrideVarName: string) {
  return (key: string) => `var(--emico-${key}, ${overrideVarName})`
}

/**
 * Get a library theme value. You probably want to use a project theme in your customer project.
 */
export const libraryTheme = Object.fromEntries(
  Object.entries(libraryThemeVars).map(([key, value]) => [key, withKey(value)]),
) as Record<
  keyof typeof libraryThemeVars,
  /**
   * This function can be used to provide a themeable categorized css variable.
   * @param overrideVarKey Specify a key name for this var. This allows users to override this
   * instance of the css var. For example, a Dot in a slider could use the accent color like this:
   * ```
   * const Dot = styled(ButtonUnstyled)`
   *   background: ${libraryTheme.accentColor('component-slider-dot-color')}
   * `
   * ```
   * This way the color of the slider dot can be changed in two ways:
   *
   * By setting the accent color for all components
   * ```css
   * :root {
   *   --emico-accent-color: #F0F
   * }
   * ```
   * Or by only setting the color of the dot:
   * ```css
   * :root {
   *   --emico-component-slider-dot-color: #F0F;
   * }
   * ```
   * @returns A string value with css vars that can be used in css.
   */
  (overrideVarKey: string) => string
>
